import type { Book } from "@/data/books";
import { matchScore, type ReaderSignals } from "@/lib/recommendations";

/** Stable string hash (FNV-1a 32-bit). */
const hash = (s: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
};

const todayKey = (d = new Date()): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Pick a deterministic Daily Match for `userId` on the current local day.
 * Same user + same day = same book. Rotates at local midnight.
 */
export const pickDailyMatch = (
  books: Book[],
  signals: ReaderSignals,
  userId: string,
): { book: Book; score: number } | null => {
  const pool = books.filter(
    (b) =>
      !signals.dismissedBookIds.includes(b.id) &&
      !signals.readBookIds.includes(b.id) &&
      !signals.savedBookIds.includes(b.id),
  );
  if (pool.length === 0) return null;

  const scored = pool
    .map((b) => ({ b, score: matchScore(b, signals) }))
    .sort((a, z) => z.score - a.score);

  // Rotate among the top contenders so the daily match feels fresh.
  const topN = Math.min(15, scored.length);
  const top = scored.slice(0, topN);
  const seed = hash(`${userId}:${todayKey()}`);
  const pick = top[seed % top.length];
  return { book: pick.b, score: pick.score };
};

/** Milliseconds until next local midnight. */
export const msUntilTomorrow = (now = new Date()): number => {
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next.getTime() - now.getTime();
};

export const formatCountdown = (ms: number): string => {
  if (ms <= 0) return "0h 0m";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
};

export const getDailyMatchDateKey = todayKey;
